import React, { Component } from "react";
import Helmet from "react-helmet";
import { Card, Layout, Tabs, Select, Divider } from "antd";
const { Sider, Content } = Layout;
import { withUser } from "@unifiedpatents/sso";
import Toolbar3GPP from "./repo/toolbar-3gpp";
import RepoTable from "./repo/table";
import DateHistogram from "./repo/date-histogram";
import FieldAggregation from "./repo/field-agg";
import AppliedFilters from "../common/applied-filters";
import CallToLogin from "../common/call-to-login";
import CallToUpgrade from "../common/call-to-upgrade";
import { PUBLIC_ASSETS_URL, API_URL } from "../../config";
import { withRouter } from "react-router";
import { keepOnlyRepo3GPPQueryStrings, mapRepo3GPPQueryStringToName } from "../../helpers/url";
import { omitBy, isEmpty } from "lodash";
import { toSetString, toDateString } from "../../helpers/converters";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  verticalGutter: {
    marginBottom: 16
  }
};

class Repo3GPP extends Component {
  state = {
    activeTab: "list",
    fieldCategory: "source",
    fieldDisplaySize: 20,
    fieldDate: "meeting_date"
  };

  render() {
    const { activeTab } = this.state;
    const {
      status: { isLoggedIn },
      metadata
    } = this.props.user;
    const accessLevel = metadata.authorization.roles.indexOf("member-lte-submission-repo") >= 0 ? "full" : isLoggedIn ? "guest" : "public";
    const helmet = {
      title: "3GPP Submissions Repository",
      meta: [
        {
          name: "keywords",
          content: "technical standards, standard setting body, standard setting organization, SSO, 3GPP, 2G standards, 3G standards, 4G standards, LTE standards, 5G standards"
        },
        {
          name: "description",
          content: "This tool provides full-text search of the entire collection of 3GPP meeting notes. Use the database to complete analysis of 2G, 3G, 4G and 5G standards specifications."
        }
      ]
    };
    const REPO_3GPP_SEARCH_URL = `${API_URL}/repos/3gpp/_search`;
    const callToLoginAlert = isLoggedIn ? accessLevel === "full" ? null : <CallToUpgrade style={styles.verticalGutter} /> : <CallToLogin style={styles.verticalGutter} />;
    const filter = this._getFilter(accessLevel);
    const renderHyperLink = (text, record) => <a href={`https://s3-us-west-1.amazonaws.com/sso-document-repository/3gpp/zip/${record._id}.zip`}>{text}</a>;
    const allCols = [
      { dataIndex: "tdoc", title: "TDoc", width: 160, render: renderHyperLink },
      { dataIndex: "meeting", title: "Meeting", width: 200 },
      { dataIndex: "title", title: "Title", width: 600 },
      { dataIndex: "source", title: "Source", width: 160, render: toSetString },
      { dataIndex: "contact", title: "Contact", width: 160 },
      { dataIndex: "contact_id", title: "Contact ID", width: 120 },
      { dataIndex: "type", title: "Type", width: 120, key: "type", sorter: true },
      { dataIndex: "for", title: "For", width: 160, key: "for", sorter: true },
      { dataIndex: "abstract", title: "Abstract", width: 600 },
      { dataIndex: "secretary_remarks", title: "Secretary Remarks", width: 160 },
      { dataIndex: "agenda_item_sort_order", title: "Agenda item sort order", width: 160 },
      { dataIndex: "agenda_item", title: "Agenda item", width: 160 },
      { dataIndex: "agenda_item_description", title: "Agenda item description", width: 160 },
      { dataIndex: "tdoc_sort_order_within_agenda_item", title: "TDoc sort order within agenda item", width: 160 },
      { dataIndex: "tdoc_status", title: "TDoc Status", width: 160, key: "tdoc_status", sorter: true },
      { dataIndex: "reservation_date", title: "Reservation Date", width: 140, render: toDateString },
      { dataIndex: "meeting_date", title: "Meeting Date", width: 140, render: toDateString, key: "meeting_date", sorter: true },
      { dataIndex: "is_revision_of", title: "Is Revison Of", width: 160 },
      { dataIndex: "revised_to", title: "Revised To", width: 160 },
      { dataIndex: "release", title: "Release", width: 160 },
      { dataIndex: "spec", title: "Spec", width: 160 },
      { dataIndex: "version", title: "Version", width: 160 },
      { dataIndex: "related_wis", title: "Related WIs", width: 160, render: toSetString },
      { dataIndex: "cr", title: "CR", width: 160 },
      { dataIndex: "cr_revision", title: "CR revision", width: 160 },
      { dataIndex: "cr_category", title: "CR category", width: 160 },
      { dataIndex: "tsg_cr_pack", title: "TSG CR Pack", width: 160 },
      { dataIndex: "reply_to", title: "Reply to", width: 160 },
      { dataIndex: "to", title: "To", width: 160, render: toSetString },
      { dataIndex: "cc", title: "Cc", width: 160, render: toSetString },
      { dataIndex: "original_ls", title: "Original LS", width: 160 },
      { dataIndex: "reply_in", title: "Reply in", width: 160 },
      { dataIndex: "spec_standard", title: "Standards", width: 160 },
      { dataIndex: "is_user_equipment", title: "Technical Category", width: 160 },
      { dataIndex: "spec_group", title: "Specification Group", width: 160 },
      { dataIndex: "spec_initial_planned_release", title: "Initial Planned Release", width: 160 },
      { dataIndex: "spec_series_no", title: "Specification Series", width: 160 },
      { dataIndex: "spec_type", title: "Specification Type", width: 160 },
      { dataIndex: "spec_spec_no", title: "Specification Number", width: 160 }
    ];
    const selectedColumns = ["tdoc", "type", "title", "source", "tdoc_status", "for", "uploaded_date", "agenda_item", "is_revision_of", "revised_to", "spec", "cr"];
    return (
      <div style={styles.body}>
        <Helmet {...helmet} />
        {callToLoginAlert}
        <Layout>
          <Sider width={260} breakpoint="lg" collapsedWidth="0" style={{ background: "transparent", marginRight: 16 }}>
            <Card style={styles.verticalGutter} bodyStyle={{ textAlign: "center" }}>
              <img alt="OPEN Logo" src={`${PUBLIC_ASSETS_URL}/open_logo.png`} height={45} style={{ marginBottom: 8 }} />
              <br />
              <a href="https://support.unifiedpatents.com/hc/en-us/articles/360007551814-Video-Codec-Zone-JCT-VC-Repository" target="_new">
                Learn More
              </a>
            </Card>
            <Toolbar3GPP accessLevel={accessLevel} esUrl={REPO_3GPP_SEARCH_URL} />
          </Sider>
          <Content>
            <AppliedFilters fieldSelector={keepOnlyRepo3GPPQueryStrings} fieldMapper={mapRepo3GPPQueryStringToName} />
            <Tabs size="small" activeKey={activeTab} onChange={val => this.setState({ activeTab: val })} tabBarStyle={{ marginBottom: 0 }}>
              <Tabs.TabPane tab="TDoc List" key="list" />
              <Tabs.TabPane tab="Analytics" key="analytics" disabled={false} />
            </Tabs>
            {activeTab === "list" && (
              <RepoTable
                filter={filter}
                accessLevel={accessLevel}
                esUrl={REPO_3GPP_SEARCH_URL}
                allCols={allCols}
                selectedColumns={selectedColumns}
                defaultSort="-meeting_date"
                tableKey="repo3GPP"
                rowKey="tdoc"
                key={JSON.stringify(filter)}
              />
            )}
            {activeTab === "analytics" && (
              <Card>
                <div style={{ textAlign: "center" }}>
                  <Select
                    value={this.state.fieldCategory}
                    placeholder="Drill-Down By"
                    onChange={val => this.setState({ fieldCategory: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 160 }}
                  >
                    {[
                      { value: "type", label: "Type" },
                      { value: "for", label: "For" },
                      { value: "tdoc_status", label: "TDoc Status" },
                      { value: "spec_standard", label: "Standards" },
                      { value: "is_user_equipment", label: "Technical Category" },
                      { value: "meeting", label: "Meeting" },
                      { value: "source", label: "Source" },
                      { value: "contact", label: "Contact" },
                      { value: "spec_group", label: "Specification Group" },
                      { value: "spec_initial_planned_release", label: "Initial Planned Release" },
                      { value: "spec_series_no", label: "Specification Series" },
                      { value: "spec_type", label: "Specification Type" },
                      { value: "spec_spec_no", label: "Specification Number" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDrilldownCategory}
                    placeholder="Drill-Down By"
                    onChange={val => this.setState({ fieldDrilldownCategory: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 160 }}
                  >
                    {[
                      { value: "", label: "None" },
                      { value: "type", label: "Type" },
                      { value: "for", label: "For" },
                      { value: "tdoc_status", label: "TDoc Status" },
                      { value: "spec_standard", label: "Standards" },
                      { value: "is_user_equipment", label: "Technical Category" },
                      { value: "meeting", label: "Meeting" },
                      { value: "source", label: "Source" },
                      { value: "contact", label: "Contact" },
                      { value: "spec_group", label: "Specification Group" },
                      { value: "spec_initial_planned_release", label: "Initial Planned Release" },
                      { value: "spec_series_no", label: "Specification Series" },
                      { value: "spec_type", label: "Specification Type" },
                      { value: "spec_spec_no", label: "Specification Number" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDisplaySize}
                    placeholder="Display Size"
                    onChange={val => this.setState({ fieldDisplaySize: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 130 }}
                  >
                    {[20, 50, 100].map(e => (
                      <Select.Option value={e} key={e}>{`Show Top ${e}`}</Select.Option>
                    ))}
                  </Select>
                  <FieldAggregation
                    field={this.state.fieldCategory}
                    field2={this.state.fieldDrilldownCategory}
                    size={this.state.fieldDisplaySize}
                    filter={filter}
                    fieldsWideLabel={["meeting"]}
                    esUrl={REPO_3GPP_SEARCH_URL}
                  />
                </div>
                <Divider />
                <div style={{ textAlign: "center" }}>
                  <Select value={this.state.fieldDate} onChange={val => this.setState({ fieldDate: val })} getPopupContainer={trigger => trigger.parentNode} style={{ width: 180 }}>
                    <Select.Option value="meeting_date">{"Meeting Date"}</Select.Option>
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDrilldownDate}
                    placeholder="Drill-Down By"
                    onChange={val => this.setState({ fieldDrilldownDate: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 180 }}
                  >
                    {[
                      { value: "", label: "None" },
                      { value: "type", label: "Type" },
                      { value: "for", label: "For" },
                      { value: "tdoc_status", label: "TDoc Status" },
                      { value: "spec_standard", label: "Standards" },
                      { value: "is_user_equipment", label: "Technical Category" },
                      { value: "meeting", label: "Meeting" },
                      { value: "source", label: "Source" },
                      { value: "contact", label: "Contact" },
                      { value: "spec_group", label: "Specification Group" },
                      { value: "spec_initial_planned_release", label: "Initial Planned Release" },
                      { value: "spec_series_no", label: "Specification Series" },
                      { value: "spec_type", label: "Specification Type" },
                      { value: "spec_spec_no", label: "Specification Number" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <DateHistogram field={this.state.fieldDate} field2={this.state.fieldDrilldownDate} filter={filter} esUrl={REPO_3GPP_SEARCH_URL} />
              </Card>
            )}
          </Content>
        </Layout>
      </div>
    );
  }

  _getFilter = accessLevel => {
    let query = keepOnlyRepo3GPPQueryStrings(this.props.location.query);
    if (accessLevel !== "full") {
      if (query.full_text && query.full_text != "example") {
        query.full_text = "example";
      }
    }
    return omitBy(query, isEmpty);
  };
}

export default withUser(withRouter(Repo3GPP));
